import React, { useMemo, useState } from "react";
import { createClaimOps526EZPdf, createEvidenceIndexPdf, saveBytesAsPdf } from "@/lib/claimUtils";
import { VA_FORMS, VA_FORM_CATEGORIES } from "@/lib/vaFormsCatalog";
import { CLAIM_TYPES, findClaimType } from "@/lib/claimTypesCatalog";
import { runFormAudit, AUDIT_STATUS } from "@/lib/formAudit";
import {
  FileText, Brain, ArrowsClockwise, FilePdf, Trash, MagnifyingGlass,
  ArrowSquareOut, Books, Info, Warning, ListChecks,
  ShieldCheck, ShieldWarning, CheckCircle, XCircle, Question,
} from "@phosphor-icons/react";

const VA_FORM_INDEX = new Map(VA_FORMS.map((f) => [f.num, f]));

// ---------------------------------------------------------------------------
// FormAuditPanel — pre-submission quality-control panel that sits at the top
// of the Master Forms page. Renders the deterministic findings produced by
// `runFormAudit()`. Severity → tone mapping is kept here (presentation only);
// the audit engine itself is UI-free.
// ---------------------------------------------------------------------------
const SEV_TONE = {
  critical: {
    chip: "#dc2626", chipBg: "rgba(220,38,38,0.10)", chipText: "#b91c1c",
    border: "rgba(220,38,38,0.32)",
    bg: "linear-gradient(95deg, rgba(220,38,38,0.10), rgba(220,38,38,0.03))",
    Icon: XCircle,
  },
  confusion: {
    chip: "#7c3aed", chipBg: "rgba(124,58,237,0.10)", chipText: "#6d28d9",
    border: "rgba(124,58,237,0.30)",
    bg: "linear-gradient(95deg, rgba(124,58,237,0.10), rgba(124,58,237,0.03))",
    Icon: Question,
  },
  review: {
    chip: "#d97706", chipBg: "rgba(217,119,6,0.10)", chipText: "#b45309",
    border: "rgba(217,119,6,0.30)",
    bg: "linear-gradient(95deg, rgba(217,119,6,0.10), rgba(217,119,6,0.03))",
    Icon: Warning,
  },
  ready: {
    chip: "#16a34a", chipBg: "rgba(16,185,129,0.10)", chipText: "#047857",
    border: "rgba(16,185,129,0.30)",
    bg: "linear-gradient(95deg, rgba(16,185,129,0.10), rgba(16,185,129,0.03))",
    Icon: CheckCircle,
  },
};

function FormAuditPanel({ audit }) {
  const [showReady, setShowReady] = useState(false);
  const overallTone = SEV_TONE[audit.overall];
  const HeroIcon = audit.overall === "ready" ? ShieldCheck : ShieldWarning;
  const overallHeadline = {
    critical:  "Critical info missing — fix before filing.",
    confusion: "Possible claim confusion — review the items below.",
    review:    "Looks workable — a few things to review.",
    ready:     "Forms look filing-ready. No issues detected.",
  }[audit.overall];

  const counts = audit.counts;
  const visible = showReady ? audit.findings : audit.findings.filter((f) => f.severity !== "ready");

  return (
    <section className="panel" data-testid="form-audit-panel" style={{ marginBottom: 16 }}>
      <div className="panel-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <ShieldCheck size={18} weight="bold" />
          <span>FORM READINESS AUDIT</span>
        </span>
        <span
          data-testid="form-audit-overall"
          data-severity={audit.overall}
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
            padding: "4px 12px", borderRadius: 999,
            background: overallTone.chip, color: "#fff",
          }}
        >
          {AUDIT_STATUS[audit.overall]}
        </span>
      </div>

      <div className="panel-body">
        {/* Hero summary */}
        <div
          style={{
            display: "flex", gap: 14, alignItems: "flex-start",
            padding: "14px 16px",
            background: overallTone.bg,
            border: `1px solid ${overallTone.border}`,
            borderRadius: 14,
          }}
          data-testid="form-audit-hero"
        >
          <HeroIcon size={26} weight="duotone" color={overallTone.chip} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>
              {overallHeadline}
            </div>
            <div style={{ marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12 }}>
              {["critical", "confusion", "review", "ready"].map((sev) => {
                const tone = SEV_TONE[sev];
                const n = counts[sev];
                return (
                  <span
                    key={sev}
                    data-testid={`form-audit-count-${sev}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "3px 10px", borderRadius: 999,
                      background: tone.chipBg, color: tone.chipText, fontWeight: 700,
                      opacity: n === 0 ? 0.45 : 1,
                    }}
                  >
                    <tone.Icon size={11} weight="fill" /> {AUDIT_STATUS[sev]}: {n}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Findings list */}
        <ul style={{ margin: "14px 0 0", padding: 0, listStyle: "none" }} data-testid="form-audit-findings">
          {visible.length === 0 && (
            <li style={{ padding: "10px 14px", fontSize: 13, color: "#475569", fontStyle: "italic" }}>
              Nothing to act on. Use "Show ready checks" below to see passed audits.
            </li>
          )}
          {visible.map((f) => {
            const tone = SEV_TONE[f.severity];
            return (
              <li
                key={f.id}
                data-testid={`form-audit-finding-${f.id}`}
                data-severity={f.severity}
                style={{
                  padding: "12px 14px",
                  marginBottom: 8,
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  borderRadius: 12,
                  display: "flex", gap: 12, alignItems: "flex-start",
                }}
              >
                <tone.Icon size={18} weight="fill" color={tone.chip} style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontSize: 9.5, fontWeight: 700, letterSpacing: "0.10em",
                        padding: "2px 8px", borderRadius: 999,
                        background: tone.chipBg, color: tone.chipText,
                      }}
                    >
                      {AUDIT_STATUS[f.severity].toUpperCase()}
                    </span>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>{f.title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 6, lineHeight: 1.55 }}>
                    <span style={{ fontWeight: 700, color: "#0f172a" }}>Why this matters: </span>{f.why}
                  </div>
                  {f.fix && (
                    <div style={{ fontSize: 12, color: "#0f172a", marginTop: 4, lineHeight: 1.55 }}>
                      <span style={{ fontWeight: 700 }}>What to do: </span>{f.fix}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {counts.ready > 0 && (
          <button
            type="button"
            className="link-btn"
            data-testid="form-audit-toggle-ready"
            onClick={() => setShowReady((p) => !p)}
            style={{ marginTop: 8, fontSize: 12 }}
          >
            {showReady ? "Hide" : "Show"} {counts.ready} passed check{counts.ready === 1 ? "" : "s"}
          </button>
        )}
      </div>
    </section>
  );
}

/** Compute "X yrs Y mo" between two ISO YYYY-MM-DD strings (or empty if invalid). */
function computeServiceLength(start, end) {
  if (!start || !end) return null;
  const a = new Date(start);
  const b = new Date(end);
  if (isNaN(a) || isNaN(b) || b < a) return null;
  let years = b.getFullYear() - a.getFullYear();
  let months = b.getMonth() - a.getMonth();
  if (b.getDate() < a.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  const totalMonths = years * 12 + months;
  return { years, months, totalMonths };
}

export default function FormsView({ master526ez, setMaster526ez, form0781, setForm0781, issues, evidence, statements, profile, workspace }) {
  const [report, setReport] = useState("");
  const serviceLength = useMemo(
    () => computeServiceLength(master526ez.serviceStart, master526ez.serviceEnd),
    [master526ez.serviceStart, master526ez.serviceEnd]
  );
  const activeClaimType = useMemo(() => findClaimType(master526ez.claimType), [master526ez.claimType]);
  const audit = useMemo(
    () => runFormAudit({ master526ez, form0781, issues, evidence: evidence || [], statements: statements || [], profile: profile || {}, workspace: workspace || {} }),
    [master526ez, form0781, issues, evidence, statements, profile, workspace],
  );

  function addMissingTo526EZ() {
    const existing = new Set((master526ez.claimedConditions || []).map((c) => c.issueId));
    const additions = issues.filter((i) => !existing.has(i.id)).map((i) => ({ issueId: i.id, claimedAs: i.claimedAs, diagnosticReference: i.code, theory: i.theory, onset: i.onset || "", notes: i.functionalImpact || "" }));
    if (!additions.length) { setReport("All conditions already synced."); return; }
    setMaster526ez({ ...master526ez, claimedConditions: [...(master526ez.claimedConditions || []), ...additions] });
    setReport(`Synced ${additions.length} condition(s) to 526EZ.`);
  }

  function removeNotOn526EZ() {
    const keepIds = new Set((master526ez.claimedConditions || []).map((c) => c.issueId));
    if (!keepIds.size) { setReport("Add conditions to the 526EZ first."); return; }
    const count = issues.filter((i) => !keepIds.has(i.id)).length;
    if (!count) { setReport("All conditions are on the 526EZ."); return; }
    setReport(`${count} condition(s) not on 526EZ. Remove them from Issues Tracker manually.`);
  }

  async function generate526EZ() {
    try {
      const bytes = await createClaimOps526EZPdf({ master526ez, profile: profile || {}, workspace: workspace || {}, issues });
      saveBytesAsPdf(bytes, "ClaimOps-526EZ-Worksheet.pdf");
      setReport("Generated 526EZ worksheet PDF.");
    } catch (err) { setReport(`Error: ${err.message}`); }
  }

  async function generateEvidenceIndex() {
    try {
      const bytes = await createEvidenceIndexPdf({ issues, evidence: [], workspace: workspace || {}, profile: profile || {} });
      saveBytesAsPdf(bytes, "ClaimOps-Evidence-Index.pdf");
      setReport("Generated evidence index PDF.");
    } catch (err) { setReport(`Error: ${err.message}`); }
  }

  return (
    <div className="view-grid" data-testid="forms-view">
      <FormAuditPanel audit={audit} />

      {/* MASTER 21-526EZ + 21-0781 worksheets — the fillable form fields. Surfaced
       *  first because this is what most veterans come to this view for. */}
      <div className="forms-cols" data-testid="forms-cols">
        <div className="panel" data-testid="form-526ez-panel">
          <div className="panel-header">
            <FileText size={18} weight="bold" />
            <span>MASTER 21-526EZ</span>
          </div>
          <div className="panel-body">
            <div className="field-grid">
              <label className="field">
                <span className="field-label">VETERAN NAME</span>
                <input className="field-input" value={master526ez.veteranName} onChange={(e) => setMaster526ez({ ...master526ez, veteranName: e.target.value })} data-testid="526-name" />
              </label>
              <label className="field">
                <span className="field-label">EMAIL</span>
                <input className="field-input" value={master526ez.email} onChange={(e) => setMaster526ez({ ...master526ez, email: e.target.value })} data-testid="526-email" />
              </label>
              <label className="field">
                <span className="field-label">DATE OF BIRTH</span>
                <input className="field-input" type="date" value={master526ez.dateOfBirth || ""} onChange={(e) => setMaster526ez({ ...master526ez, dateOfBirth: e.target.value })} data-testid="526-dob" />
              </label>
              <label className="field">
                <span className="field-label">CLAIM TYPE</span>
                <select
                  className="field-input"
                  value={master526ez.claimType || ""}
                  onChange={(e) => setMaster526ez({ ...master526ez, claimType: e.target.value })}
                  data-testid="526-claim-type"
                >
                  <option value="">— Select a claim type —</option>
                  {CLAIM_TYPES.map((c) => (
                    <option key={c.id} value={c.label}>{c.label}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span className="field-label">SERVICE START DATE</span>
                <input className="field-input" type="date" value={master526ez.serviceStart || ""} onChange={(e) => setMaster526ez({ ...master526ez, serviceStart: e.target.value })} data-testid="526-service-start" />
              </label>
              <label className="field">
                <span className="field-label">SERVICE END DATE</span>
                <input className="field-input" type="date" value={master526ez.serviceEnd || ""} onChange={(e) => setMaster526ez({ ...master526ez, serviceEnd: e.target.value })} data-testid="526-service-end" />
              </label>
            </div>
            {serviceLength && (
              <div
                className="service-length-badge"
                data-testid="526-service-length"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  marginTop: 10, padding: "6px 12px",
                  background: serviceLength.totalMonths < 6
                    ? "color-mix(in srgb, #DC2626 10%, var(--surface))"
                    : "color-mix(in srgb, var(--accent, #1D4ED8) 8%, var(--surface))",
                  border: `1px solid ${serviceLength.totalMonths < 6 ? "#DC2626" : "var(--accent, #1D4ED8)"}`,
                  borderRadius: 4,
                  fontSize: 12,
                  fontWeight: 700,
                  color: serviceLength.totalMonths < 6 ? "#7F1D1D" : "var(--text-primary)",
                }}
              >
                {serviceLength.totalMonths < 6 && <Warning size={14} weight="bold" color="#DC2626" />}
                <span data-testid="526-service-length-text">
                  SERVICE LENGTH: {serviceLength.years} yr{serviceLength.years === 1 ? "" : "s"} {serviceLength.months} mo
                </span>
                {serviceLength.totalMonths < 6 && (
                  <span data-testid="526-service-length-flag" style={{ fontWeight: 700, color: "#DC2626", textTransform: "uppercase", fontSize: 11, letterSpacing: "0.04em" }}>
                    • flag: pre-existing-condition risk
                  </span>
                )}
              </div>
            )}
            <label className="field field--full" style={{ marginTop: 12 }}>
              <span className="field-label">MAILING ADDRESS</span>
              <textarea className="field-input field-textarea" value={master526ez.mailingAddress} onChange={(e) => setMaster526ez({ ...master526ez, mailingAddress: e.target.value })} data-testid="526-address" />
            </label>
            {activeClaimType ? (
              <div className="claim-type-forms" data-testid="claim-type-forms" style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <ListChecks size={14} weight="bold" />
                  <span className="field-label" style={{ margin: 0 }}>REQUIRED FORMS FOR THIS CLAIM TYPE</span>
                </div>
                <p className="panel-desc" style={{ marginTop: 0, marginBottom: 10 }}>{activeClaimType.blurb}</p>
                <ul className="claim-type-forms-list" data-testid="claim-type-forms-list" style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  {activeClaimType.forms.map((f) => {
                    const meta = VA_FORM_INDEX.get(f.num);
                    return (
                      <li
                        key={f.num}
                        data-testid={`claim-type-form-${f.num}`}
                        style={{
                          display: "flex", alignItems: "flex-start", gap: 10,
                          padding: "8px 10px",
                          background: f.optional ? "var(--surface-2, #F8FAFC)" : "var(--surface)",
                          border: `1px solid ${f.optional ? "var(--border)" : "color-mix(in srgb, var(--accent, #1D4ED8) 30%, transparent)"}`,
                          borderRadius: 4,
                        }}
                      >
                        <span className="mono" style={{ fontWeight: 700, minWidth: 80, fontSize: 12 }}>{f.num}</span>
                        <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                            {meta?.title || f.role}
                            {f.optional && (
                              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                                optional
                              </span>
                            )}
                          </div>
                          <div style={{ color: "var(--text-secondary)", marginTop: 1 }}>{f.role}</div>
                        </div>
                        {meta?.pdf && (
                          <a
                            href={meta.pdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn--outline btn--sm"
                            data-testid={`claim-type-form-pdf-${f.num}`}
                            style={{ flexShrink: 0 }}
                          >
                            <FilePdf size={11} weight="bold" /> PDF <ArrowSquareOut size={9} weight="bold" />
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div className="empty-state" data-testid="claim-type-forms-empty" style={{ marginTop: 12, padding: "12px 14px" }}>
                <Info size={14} weight="bold" />
                <span style={{ marginLeft: 6, fontSize: 12 }}>Pick a CLAIM TYPE above to see exactly which VA forms you need to submit.</span>
              </div>
            )}
            <div className="form-conditions-list" data-testid="526-conditions-list">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span className="field-label">CLAIMED CONDITIONS ON 526EZ</span>
                {(master526ez.claimedConditions || []).length > 0 && (
                  <button className="btn btn--danger btn--sm" onClick={() => setMaster526ez((prev) => ({ ...prev, claimedConditions: [] }))} data-testid="526-clear-all-btn">
                    <Trash size={12} weight="bold" /> CLEAR ALL
                  </button>
                )}
              </div>
              {(master526ez.claimedConditions || []).length === 0 && <p className="panel-desc">No conditions synced. Click "Sync Conditions" above.</p>}
              {[...(master526ez.claimedConditions || [])].sort((a, b) => (a.claimedAs || "").localeCompare(b.claimedAs || "")).map((c, i) => (
                <div key={c.issueId || i} className="form-condition-row" data-testid={`526-condition-${i}`}>
                  <span className="mono">{i + 1}.</span>
                  <span>{c.claimedAs}</span>
                  <span className="form-condition-theory">{c.theory}</span>
                  <button
                    className="btn btn--danger btn--sm form-condition-del"
                    onClick={() => setMaster526ez((prev) => ({ ...prev, claimedConditions: (prev.claimedConditions || []).filter((_, idx) => idx !== i) }))}
                    data-testid={`526-condition-del-${i}`}
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="panel" data-testid="form-0781-panel">
          <div className="panel-header">
            <Brain size={18} weight="bold" />
            <span>MASTER 21-0781</span>
          </div>
          <div className="panel-body">
            <p className="panel-desc">
              For PTSD or mental-health claims involving traumatic events. To establish the stressor, your narrative needs to cover:
            </p>
            <ul className="form-helper-list" style={{ margin: "8px 0 14px", paddingLeft: 18, color: "var(--text-secondary, #475569)", fontSize: 12.5, lineHeight: 1.6 }}>
              <li><strong>What</strong> happened (specific incident or pattern)</li>
              <li><strong>When</strong> it happened (date or month/year)</li>
              <li><strong>Where</strong> it happened (base, country, AO)</li>
              <li><strong>Unit / ship</strong> you were assigned to at the time</li>
              <li><strong>Who</strong> can verify it (buddy statements, command, witness)</li>
              <li>Symptoms that started afterward (and when they began)</li>
              <li>Current <strong>occupational and social impact</strong> — work, family, daily life</li>
            </ul>
            <div className="field-grid">
              <label className="field">
                <span className="field-label">VETERAN NAME</span>
                <input className="field-input" value={form0781.veteranName} onChange={(e) => setForm0781({ ...form0781, veteranName: e.target.value })} data-testid="0781-name" />
              </label>
              <label className="field field--full">
                <span className="field-label">MENTAL HEALTH CONDITIONS</span>
                <input className="field-input" value={form0781.mentalHealthConditions} onChange={(e) => setForm0781({ ...form0781, mentalHealthConditions: e.target.value })} data-testid="0781-conditions" />
              </label>
            </div>
            <label className="field field--full" style={{ marginTop: 12 }}>
              <span className="field-label">EVENT DESCRIPTION</span>
              <textarea
                className="field-input field-textarea"
                rows={4}
                value={form0781.eventDescription}
                onChange={(e) => setForm0781({ ...form0781, eventDescription: e.target.value })}
                placeholder="What happened, when (date or month/year), where (base/country/AO), unit or ship, and who can verify it. Be specific — vague stressors are the #1 reason 21-0781s come back."
                data-testid="0781-event"
              />
            </label>
            <label className="field field--full" style={{ marginTop: 12 }}>
              <span className="field-label">CURRENT IMPACT</span>
              <textarea
                className="field-input field-textarea"
                rows={3}
                value={form0781.currentImpact}
                onChange={(e) => setForm0781({ ...form0781, currentImpact: e.target.value })}
                placeholder="Describe today's occupational and social impact: missed work, conflicts, relationship strain, isolation, sleep, panic episodes, medication."
                data-testid="0781-impact"
              />
            </label>
            <div className="field-grid" style={{ marginTop: 12 }}>
              <label className="field">
                <span className="field-label">EVENT DATE</span>
                <input className="field-input" value={form0781.eventDate} onChange={(e) => setForm0781({ ...form0781, eventDate: e.target.value })} placeholder="YYYY-MM" data-testid="0781-date" />
              </label>
              <label className="field">
                <span className="field-label">LOCATION</span>
                <input className="field-input" value={form0781.eventLocation} onChange={(e) => setForm0781({ ...form0781, eventLocation: e.target.value })} data-testid="0781-location" />
              </label>
              <label className="field">
                <span className="field-label">UNIT / SHIP</span>
                <input className="field-input" value={form0781.unitAssignment} onChange={(e) => setForm0781({ ...form0781, unitAssignment: e.target.value })} data-testid="0781-unit" />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Document generators sit under the forms so they remain accessible
       *  but don't push the fillable worksheets off-screen. */}
      <div className="panel" data-testid="pdf-gen-panel">
        <div className="panel-header">
          <FilePdf size={18} weight="bold" />
          <span>GENERATE DOCUMENTS</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc">Generate PDF worksheets from your claim data. These are planning documents — verify against official VA forms before submission.</p>
          <div className="btn-row">
            <button className="btn btn--accent" onClick={generate526EZ} data-testid="gen-526ez-btn">
              <FilePdf size={16} weight="bold" /> CREATE 526EZ PDF
            </button>
            <button className="btn btn--outline" onClick={generateEvidenceIndex} data-testid="gen-evidence-index-btn">
              <FilePdf size={16} weight="bold" /> EVIDENCE INDEX PDF
            </button>
          </div>
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button className="btn btn--outline" onClick={addMissingTo526EZ} data-testid="sync-conditions-btn">
              <ArrowsClockwise size={16} weight="bold" /> SYNC CONDITIONS
            </button>
            <button className="btn btn--ghost" onClick={removeNotOn526EZ} data-testid="remove-unsynced-btn">
              REMOVE UNSYNCED
            </button>
          </div>
          {report && <div className="form-report" data-testid="form-report">{report}</div>}
        </div>
      </div>

      {/* Full VA forms library — searchable directory of all 68 official PDFs. */}
      <VaFormsLibrary />
    </div>
  );
}

function VaFormsLibrary() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all"); // 'all' | category id

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return VA_FORMS.filter((f) => {
      if (cat !== "all" && f.cat !== cat) return false;
      if (!q) return true;
      const haystack = [
        f.num, f.title, f.blurb, ...(f.tags || []),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [query, cat]);

  // Group filtered list by category (preserving the order defined in VA_FORM_CATEGORIES)
  const grouped = useMemo(() => {
    const m = new Map();
    VA_FORM_CATEGORIES.forEach((c) => m.set(c.id, []));
    filtered.forEach((f) => {
      if (!m.has(f.cat)) m.set(f.cat, []);
      m.get(f.cat).push(f);
    });
    return VA_FORM_CATEGORIES
      .map((c) => ({
        ...c,
        items: (m.get(c.id) || []).slice().sort((a, b) => (a.name || a.code || "").localeCompare(b.name || b.code || "")),
      }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  return (
    <div className="panel" data-testid="va-forms-library">
      <div className="panel-header">
        <Books size={18} weight="bold" />
        <span>VA FORMS LIBRARY</span>
        <span className="header-count mono">{VA_FORMS.length}</span>
      </div>
      <div className="panel-body">
        <p className="panel-desc">
          Direct download links to every major VA form — disability, healthcare, education, home loan, burial, appeals, and more. All links open the official VA PDF in a new tab.
        </p>
        <div className="vaforms-toolbar">
          <div className="vaforms-search-wrap">
            <MagnifyingGlass size={13} weight="bold" className="vaforms-search-icon" />
            <input
              className="field-input vaforms-search-input"
              placeholder="Search by form number, title, or keyword..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="vaforms-search"
            />
          </div>
          <select
            className="field-input vaforms-cat-select"
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="vaforms-cat-select"
          >
            <option value="all">All categories</option>
            {VA_FORM_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <span className="vaforms-count mono" data-testid="vaforms-count">
            {filtered.length} form{filtered.length === 1 ? "" : "s"}
          </span>
        </div>

        {filtered.length === 0 && (
          <div className="empty-state" data-testid="vaforms-empty">
            No forms match "{query}". Try a different keyword or category.
          </div>
        )}

        <div className="vaforms-groups">
          {grouped.map((g) => (
            <div key={g.id} className="vaforms-group" data-testid={`vaforms-group-${g.id}`}>
              <h3 className="vaforms-group-title">{g.label}</h3>
              <div className="vaforms-grid">
                {g.items.map((f) => (
                  <div key={f.num + f.title} className="vaform-card" data-testid={`vaform-${f.num}`}>
                    <div className="vaform-card-head">
                      <span className="vaform-num mono">{f.num}</span>
                      {(f.tags || []).map((t) => (
                        <span key={t} className="vaform-tag">{t}</span>
                      ))}
                    </div>
                    <div className="vaform-title">{f.title}</div>
                    <div className="vaform-blurb">{f.blurb}</div>
                    <div className="vaform-actions">
                      <a
                        href={f.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--accent btn--sm"
                        data-testid={`vaform-pdf-${f.num}`}
                      >
                        <FilePdf size={11} weight="bold" /> DOWNLOAD PDF <ArrowSquareOut size={10} weight="bold" />
                      </a>
                      {f.info && (
                        <a
                          href={f.info}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn--outline btn--sm"
                          data-testid={`vaform-info-${f.num}`}
                        >
                          <Info size={11} weight="bold" /> INFO
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="vaforms-footer">
          <Info size={11} weight="bold" />
          <span>
            Can't find what you need? The official VA forms index at{" "}
            <a href="https://www.va.gov/find-forms/" target="_blank" rel="noopener noreferrer">va.gov/find-forms</a>{" "}
            has the complete catalog (1,000+ forms).
          </span>
        </div>
      </div>
    </div>
  );
}
